import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Users,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  ChevronRight,
  RefreshCw,
  User,
  Calendar,
  Dumbbell,
  Pencil,
  Check,
  X,
  Search,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { API, avatarUrl } from '../utils/api';
import { toast } from 'sonner';

const StatusCard = ({ title, count, icon: Icon, color, bgColor, borderColor, onClick }) => (
  <motion.div
    onClick={onClick}
    className={`bg-zinc-900/50 border ${borderColor} rounded-2xl p-5 cursor-pointer hover:bg-zinc-800/50 transition-all`}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-zinc-400 text-sm font-medium mb-1">{title}</p>
        <p className={`font-primary font-black text-4xl ${color}`}>{count}</p>
      </div>
      <div className={`w-14 h-14 rounded-xl ${bgColor} flex items-center justify-center`}>
        <Icon className={`w-7 h-7 ${color}`} />
      </div>
    </div>
  </motion.div>
);

const StudentItem = ({ student }) => {
  const [editing, setEditing] = useState(false);
  const [goal, setGoal] = useState(student.monthly_goal || 4);
  const [done, setDone] = useState(student.workouts_this_month || 0);
  const [saving, setSaving] = useState(false);
  const [savedGoal, setSavedGoal] = useState(student.monthly_goal || 4);
  const [savedDone, setSavedDone] = useState(student.workouts_this_month || 0);

  const getStatusBadge = () => {
    switch (student.status) {
      case 'sem_treino':
        return <span className="text-xs bg-zinc-700 text-zinc-300 px-2 py-1 rounded-full">Sem treino</span>;
      case 'vencido':
        return <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded-full">Vencido há {student.days_expired} dia(s)</span>;
      case 'vencendo':
        return <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded-full">Vence em {student.days_until_expiry} dia(s)</span>;
      case 'ativo':
        return <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">Ativo</span>;
      default:
        return null;
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/users/${student.id}`, {
        monthly_goal: goal,
        workouts_this_month: done,
      });
      toast.success(`Meta de ${student.name} atualizada!`);
      setSavedGoal(goal);
      setSavedDone(done);
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setGoal(savedGoal);
    setDone(savedDone);
    setEditing(false);
  };

  return (
    <div className="flex items-center justify-between py-3 border-b border-white/5 last:border-b-0">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full overflow-hidden bg-zinc-800 flex items-center justify-center">
          {student.avatar_v ? (
            <img src={avatarUrl(student.id, student.avatar_v)} alt={student.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
          ) : (
            <User className="w-5 h-5 text-zinc-500" />
          )}
        </div>
        <div>
          <p className="font-bold text-white">{student.name}</p>
          <p className="text-xs text-zinc-500">{student.email}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {getStatusBadge()}
        {editing ? (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-zinc-800 rounded-lg px-2 py-1">
              <input
                type="number"
                value={done}
                onChange={(e) => setDone(Math.max(0, parseInt(e.target.value) || 0))}
                className="w-10 bg-transparent text-white text-center text-sm font-bold focus:outline-none"
                min="0"
                data-testid={`edit-done-${student.id}`}
              />
              <span className="text-zinc-500 text-sm">/</span>
              <input
                type="number"
                value={goal}
                onChange={(e) => setGoal(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-10 bg-transparent text-white text-center text-sm font-bold focus:outline-none"
                min="1"
                data-testid={`edit-goal-${student.id}`}
              />
            </div>
            <button onClick={handleSave} disabled={saving} className="p-1.5 rounded-md bg-neon-lime/20 text-neon-lime hover:bg-neon-lime/30 transition-colors" data-testid={`save-meta-${student.id}`}>
              <Check className="w-4 h-4" />
            </button>
            <button onClick={handleCancel} className="p-1.5 rounded-md bg-zinc-800 text-zinc-400 hover:text-white transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-1.5 text-sm font-medium text-zinc-400 hover:text-white transition-colors group"
            data-testid={`meta-${student.id}`}
          >
            <span className={`${savedDone >= savedGoal ? 'text-neon-lime' : 'text-white'}`}>
              {savedDone}/{savedGoal}
            </span>
            {' '}treinos
            <Pencil className="w-3 h-3 text-zinc-600 group-hover:text-neon-lime transition-colors" />
          </button>
        )}
        <Link
          to={`/admin/users/${student.id}/workouts`}
          className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium h-9 px-3 text-neon-lime hover:bg-neon-lime/10 no-underline transition-colors"
        >
          Gerenciar <ChevronRight className="w-4 h-4 ml-1" />
        </Link>
      </div>
    </div>
  );
};

const StudentSection = ({ title, icon: Icon, color, students, emptyMessage }) => (
  <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5">
    <div className="flex items-center gap-3 mb-4">
      <Icon className={`w-6 h-6 ${color}`} />
      <h3 className="font-bold text-white text-lg">{title}</h3>
      <span className="text-zinc-500 text-sm">({students.length})</span>
    </div>
    {students.length === 0 ? (
      <p className="text-zinc-500 text-center py-6">{emptyMessage}</p>
    ) : (
      <div className="max-h-[300px] overflow-y-auto">
        {students.map((student) => (
          <StudentItem key={student.id} student={student} />
        ))}
      </div>
    )}
  </div>
);

export const StudentsStatusDashboard = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filterStudents = useCallback(
    (students) => {
      if (!searchTerm.trim()) return students || [];
      const term = searchTerm.trim().toLowerCase();
      return (students || []).filter(
        (s) =>
          (s.name && s.name.toLowerCase().includes(term)) ||
          (s.email && s.email.toLowerCase().includes(term))
      );
    },
    [searchTerm]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/students-status`);
      setData(res.data);
    } catch (error) {
      console.error('Error fetching students status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-neon-lime" />
      </div>
    );
  }

  const summary = data?.summary || {};

  const filteredWithout = filterStudents(data?.students_without_workout);
  const filteredExpired = filterStudents(data?.students_expired);
  const filteredExpiring = filterStudents(data?.students_expiring_soon);
  const filteredActive = filterStudents(data?.students_active);

  const totalFiltered =
    filteredWithout.length +
    filteredExpired.length +
    filteredExpiring.length +
    filteredActive.length;

  const isSearching = searchTerm.trim().length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Dumbbell className="w-8 h-8 text-neon-lime" />
          <div>
            <h2 className="font-primary font-black text-2xl text-white uppercase">
              Status dos Alunos
            </h2>
            <p className="text-zinc-500 text-sm">Acompanhe os treinos de todos os alunos</p>
          </div>
        </div>
        <Button onClick={fetchData} variant="outline" className="border-zinc-700 text-zinc-400 hover:text-white">
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="w-5 h-5 text-zinc-500 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Pesquisar aluno por nome ou email..."
          className="w-full bg-zinc-900/70 border border-white/10 rounded-2xl pl-12 pr-12 py-3.5 text-white placeholder-zinc-500 focus:outline-none focus:border-neon-lime/60 focus:ring-2 focus:ring-neon-lime/20 transition-all"
          data-testid="admin-students-search-input"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
            aria-label="Limpar pesquisa"
            data-testid="admin-students-search-clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {isSearching && (
        <p className="text-sm text-zinc-400" data-testid="admin-students-search-summary">
          {totalFiltered === 0
            ? 'Nenhum aluno encontrado'
            : `${totalFiltered} aluno(s) encontrado(s) para "${searchTerm}"`}
        </p>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatusCard
          title="Sem Treino"
          count={summary.without_workout || 0}
          icon={XCircle}
          color="text-zinc-400"
          bgColor="bg-zinc-800"
          borderColor="border-zinc-700"
          onClick={() => setActiveSection('without')}
        />
        <StatusCard
          title="Vencidos"
          count={summary.expired || 0}
          icon={AlertTriangle}
          color="text-red-400"
          bgColor="bg-red-500/10"
          borderColor="border-red-500/30"
          onClick={() => setActiveSection('expired')}
        />
        <StatusCard
          title="Vencendo (7 dias)"
          count={summary.expiring_soon || 0}
          icon={Clock}
          color="text-yellow-400"
          bgColor="bg-yellow-500/10"
          borderColor="border-yellow-500/30"
          onClick={() => setActiveSection('expiring')}
        />
        <StatusCard
          title="Ativos"
          count={summary.active || 0}
          icon={CheckCircle}
          color="text-green-400"
          bgColor="bg-green-500/10"
          borderColor="border-green-500/30"
          onClick={() => setActiveSection('active')}
        />
      </div>

      {/* Quick Actions */}
      <div className="flex gap-3 flex-wrap">
        <Button
          onClick={() => setActiveSection('all')}
          variant={activeSection === 'all' ? 'default' : 'outline'}
          className={activeSection === 'all' ? 'bg-neon-lime text-black' : 'border-zinc-700 text-zinc-400'}
        >
          Todos
        </Button>
        <Button
          onClick={() => setActiveSection('without')}
          variant={activeSection === 'without' ? 'default' : 'outline'}
          className={activeSection === 'without' ? 'bg-zinc-600 text-white' : 'border-zinc-700 text-zinc-400'}
        >
          Sem Treino ({summary.without_workout || 0})
        </Button>
        <Button
          onClick={() => setActiveSection('expired')}
          variant={activeSection === 'expired' ? 'default' : 'outline'}
          className={activeSection === 'expired' ? 'bg-red-500 text-white' : 'border-zinc-700 text-zinc-400'}
        >
          Vencidos ({summary.expired || 0})
        </Button>
        <Button
          onClick={() => setActiveSection('expiring')}
          variant={activeSection === 'expiring' ? 'default' : 'outline'}
          className={activeSection === 'expiring' ? 'bg-yellow-500 text-black' : 'border-zinc-700 text-zinc-400'}
        >
          Vencendo ({summary.expiring_soon || 0})
        </Button>
        <Button
          onClick={() => setActiveSection('active')}
          variant={activeSection === 'active' ? 'default' : 'outline'}
          className={activeSection === 'active' ? 'bg-green-500 text-white' : 'border-zinc-700 text-zinc-400'}
        >
          Ativos ({summary.active || 0})
        </Button>
      </div>

      {/* Student Sections */}
      <div className="space-y-6">
        {isSearching && totalFiltered === 0 ? (
          <div
            className="bg-zinc-900/50 border border-white/5 rounded-2xl p-10 text-center"
            data-testid="admin-students-empty-state"
          >
            <Search className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400 font-medium">Nenhum aluno encontrado</p>
            <p className="text-zinc-600 text-sm mt-1">
              Tente outro nome ou email
            </p>
          </div>
        ) : (
          <>
            {(activeSection === 'all' || activeSection === 'without') && (
              <StudentSection
                title="Alunos Sem Treino"
                icon={XCircle}
                color="text-zinc-400"
                students={filteredWithout}
                emptyMessage={isSearching ? 'Nenhum aluno encontrado nesta seção' : 'Todos os alunos têm treino atribuído'}
              />
            )}

            {(activeSection === 'all' || activeSection === 'expired') && (
              <StudentSection
                title="Treinos Vencidos"
                icon={AlertTriangle}
                color="text-red-400"
                students={filteredExpired}
                emptyMessage={isSearching ? 'Nenhum aluno encontrado nesta seção' : 'Nenhum treino vencido'}
              />
            )}

            {(activeSection === 'all' || activeSection === 'expiring') && (
              <StudentSection
                title="Vencendo nos Próximos 7 Dias"
                icon={Clock}
                color="text-yellow-400"
                students={filteredExpiring}
                emptyMessage={isSearching ? 'Nenhum aluno encontrado nesta seção' : 'Nenhum treino vencendo em breve'}
              />
            )}

            {(activeSection === 'all' || activeSection === 'active') && (
              <StudentSection
                title="Treinos Ativos"
                icon={CheckCircle}
                color="text-green-400"
                students={filteredActive}
                emptyMessage={isSearching ? 'Nenhum aluno encontrado nesta seção' : 'Nenhum aluno com treino ativo'}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
